import { useEffect, useReducer, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import PolicySummary from './PolicySummary';
import { COASTAL_MESSAGES, PAGES, POLICY_ENUMS, POLICY_NAMES, POLICY_OPTIONS } from '../../../constants';
import {
  selectApplication,
  selectBPPDeductible,
  selectFirstLocation,
  selectGLDeductible,
  selectGLLimit,
  selectGrossAnnualSales,
  selectIsRetroactiveDateChecked,
  selectLocationBPPLimit,
  selectLocationBuildingLimit,
  selectPlDeductible,
  selectPlLimit,
  selectPolicyTypes,
  selectProfessionalLiability,
} from '../../../selectors/application';
import usePLLimitOptions from '../../../hooks/usePLLimitOptions';
import useGetApplicationOptions from '../../../hooks/useGetApplicationOptions';
import { setLocationValidationError } from '../../../actions/application';
import { formatCurrency, formatWindHailDeductible } from '../../../helpers/formatNumber';
import toolTipContent, { windHailDeductibleDescription } from '../../../helpers/toolTipContent';
import { getQuoteAmount, getQuoteAmountByType, selectExclusions, selectQuotes } from '../../../selectors/quote';
import { selectLoadingState, selectReasonabilityErrors } from '../../../selectors';
import {
  setBppDeductible,
  setGlLimit,
  updateProfessionalLiability,
  updateLocation,
  setApplicationTypes,
} from '../../../actions/application';
import handleUpdateApplication from '../../../actions/handleUpdateApplication';
import { getBuildingLimitErrorMessage } from '../../../helpers/locationLimitErrors';
import { SectionDivider, SectionHeader, SectionHeaderCont } from '../styles';
import {
  WhiteHr,
  PolicySummaryCont,
  StyledSmoothCollapse,
  DrawerControls,
  ErrorPopUp,
  ExclamationMark,
  SectionCont,
  HeaderSubCont,
  PolicyTitle,
  PolicyPrice,
} from './styles';
import coterie_bang from '../../../icons/coterie_bang.svg';
import caretUp from '../../../icons/caret_up.svg';
import { PolicyIcon } from '../../../elements';
import policyInfo from '../../PolicySelection/policyInfo';
import { summariesAttributesFactory } from './factories';
import PolicyItem from './PolicyItem';
import { ExclusionsHeader, ExclusionsList, ExclusionsItem } from './styles/policySummaryCards';
import { useFeatureFlags } from '../../../toggle_tools/featureFlagTools';
import styled from 'styled-components';
import isApplicationBindable from '../../../helpers/isApplicationBindable';
import { getCurrentPage } from '../../../selectors/routing';
import Checkbox from '../../../elements/Checkbox';
import AddPolicy from '../AddPolicy';

type quote = { monthly: string; annual: string };
interface QuoteAmounts {
  [key: string]: quote;
}

interface PolicySummaryCardProps {
  isLoading: boolean;
  businessLocation: ApplicationLocation;
  applicationTypes: string[];
  buildingLimit?: number;
  bppLimit?: number;
  bppDeductible?: number;
  glLimit?: number;
  propertyDamageLiabilityDeductible?: number;
  plLimit: number | null;
  plDeductible: number | null;
  professionalLiability: Object;
  grossAnnualSales?: number;
  quoteAmountByType: QuoteAmounts;
  exclusions: string[];
  retroactiveDate?: string;
  setBppDeductible: (num: number) => void;
  setGlLimit: (num: number) => void;
  updateProfessionalLiability: (val: any) => void;
  updateLocation: (val: any) => void;
  setApplicationTypes: (types: string[]) => void;
  handleUpdateApplication: () => void;
  quotes: Quote[];
  reasonabilityErrors: string[];
  isRetroActiveDateChecked: boolean;
  isBindable: boolean;
  quoteAmount: string;
}

const PolicySummaryCards = ({
  isLoading,
  businessLocation,
  applicationTypes,
  bppLimit,
  buildingLimit,
  bppDeductible,
  glLimit,
  propertyDamageLiabilityDeductible,
  plLimit,
  plDeductible,
  professionalLiability,
  grossAnnualSales,
  quoteAmountByType,
  exclusions,
  setBppDeductible,
  setGlLimit,
  updateProfessionalLiability,
  updateLocation,
  setApplicationTypes,
  handleUpdateApplication,
  quotes,
  reasonabilityErrors,
  isRetroActiveDateChecked,
  isBindable,
  quoteAmount,
}: PolicySummaryCardProps) => {
  const [expanded, setExpanded] = useState('');
  const [editExpanded, setEditExpanded] = useState('');
  const [showErrorMsg, setShowErrorMsg] = useState('');
  const [shouldShowErrorMsg, setShouldShowErrorMsg] = useState(false);
  const [bppLimitError, setBppLimitError] = useState<{ value: number; message: string } | null>(null);
  const [buildingLimitError, setBuildingLimitError] = useState<{ value: number; message: string } | null>(null);

  const summaryDispatch = useDispatch();

  const { showPlv2, feWindHailDeductibleFromApi } = useFeatureFlags();
  const { data: applicationOptions, isError, isFetching: isFetchingApplicationOptions } = useGetApplicationOptions();

  const isMissingOptionsData =
    !isFetchingApplicationOptions &&
    !isError &&
    (applicationOptions?.bppLimit?.max == null ||
      applicationOptions?.bppLimit?.min == null ||
      !applicationOptions?.bppDeductible?.allowedValues?.length);

  const isBop = applicationTypes.includes(POLICY_ENUMS.BOP);
  // Wind/hail split (extra row + "All Other Perils"/"Wind and Hail" labels) used to be FL-only.
  // With the flag on, the applicationOptions response decides instead of the state.
  const apiWindHailDeductible = applicationOptions?.windHailDeductible?.default;
  const hasWindHailDeductible = feWindHailDeductibleFromApi
    ? Boolean(apiWindHailDeductible)
    : businessLocation?.state === 'FL';

  useEffect(() => {
    summaryDispatch(
      setLocationValidationError(
        isBop && Boolean(bppLimitError || buildingLimitError || isError || isMissingOptionsData)
      )
    );
  }, [isBop, bppLimitError, buildingLimitError, summaryDispatch, isError, isMissingOptionsData]);

  const { occurrenceLimitOptions, occurrenceToAggregateMap, isLoading: isLoadingPLOptions } = usePLLimitOptions();

  const renderExclusions = (policyType: string) => {
    return policyType === 'BOP' || policyType === 'GL' ? Boolean(exclusions?.length) : false;
  };

  const [cachedSelectedPolicies, dispatchHook] = useReducer(
    (state: any, action: any) => (action != null ? action : state),
    []
  );

  useEffect(() => {
    const hasNewPolicyAdded = applicationTypes.length > cachedSelectedPolicies.length;
    const hasUpgradedGLtoBOP =
      applicationTypes.includes(POLICY_ENUMS.BOP) && !cachedSelectedPolicies.includes(POLICY_ENUMS.BOP);
    if (hasNewPolicyAdded || hasUpgradedGLtoBOP) {
      dispatchHook(applicationTypes);
    }
  }, [applicationTypes, cachedSelectedPolicies]);

  const togglePolicyEdit = (section: string) => {
    if (editExpanded === section) {
      setEditExpanded('');
    } else {
      setEditExpanded(section);
    }
  };

  const updateLocationProperty = (update: { bppLimit: number } | { buildingLimit: number }) => {
    updateLocation({ ...businessLocation, ...update });
    handleUpdateApplication();
  };

  const bppLimitMax = applicationOptions?.bppLimit?.max;
  const bppLimitMin = applicationOptions?.bppLimit?.min;
  const hasCoastalBppRestriction = applicationOptions?.bppLimit?.hasCoastalRestriction;
  const buildingLimitMax = applicationOptions?.buildingLimit?.max;
  const buildingLimitMin = applicationOptions?.buildingLimit?.min;

  const updateBppLimit = debounce((event: { floatValue: number }) => {
    if (event.floatValue == null) return;
    const isOverLimit = bppLimitMax ? event.floatValue > bppLimitMax : false;
    const isUnderLimit = bppLimitMin ? event.floatValue < bppLimitMin : false;

    const belowCoastalMessage = `Coverage must be below ${formatCurrency(
      bppLimitMax
    )} due to proximity to the coastline`;
    const belowMessage = `Coverage must be less than ${formatCurrency(bppLimitMax)}`;

    if (isOverLimit) {
      const message = hasCoastalBppRestriction ? belowCoastalMessage : belowMessage;
      setBppLimitError({ value: event.floatValue, message });
    } else if (isUnderLimit) {
      const message = `Coverage must be at least ${formatCurrency(bppLimitMin)}`;
      setBppLimitError({ value: event.floatValue, message });
    } else {
      setBppLimitError(null);
      updateLocationProperty({ bppLimit: event.floatValue });
    }
  }, 500);

  const updateBuildingLimit = debounce((event: { floatValue: number }) => {
    if (event.floatValue == null) return;
    const message = getBuildingLimitErrorMessage(event.floatValue, buildingLimitMin, buildingLimitMax);

    if (message) {
      setBuildingLimitError({ value: event.floatValue, message });
    } else {
      setBuildingLimitError(null);
      updateLocationProperty({ buildingLimit: event.floatValue });
    }
  }, 500);

  // Needed to add this useEffect to fix recursive bug with application and bindable api
  useEffect(() => {
    return () => {
      updateBppLimit.cancel();
      updateBuildingLimit.cancel();
    };
  }, [updateBppLimit, updateBuildingLimit]);

  const updateBppDeductible = (value: number) => {
    setBppDeductible(value);
    handleUpdateApplication();
  };

  const updateRetroactiveDate = (date: string) => {
    handleUpdateApplication();
  };

  const updateGlLimit = (value: number) => {
    setGlLimit(value);
    handleUpdateApplication();
  };

  const updatePlLimit = (value: number) => {
    const updates = {
      occurrenceLimit: value,
      aggregateLimit: occurrenceToAggregateMap[value],
    };
    const updatedProfessionalLiability = Object.assign({}, professionalLiability, { ...updates });

    updateProfessionalLiability({ ...updatedProfessionalLiability });
    handleUpdateApplication();
  };

  const updatePlDeductible = (value: number) => {
    const updates = { deductibleAmount: value };
    const updatedProfessionalLiability = Object.assign({}, professionalLiability, { ...updates });

    updateProfessionalLiability({ ...updatedProfessionalLiability });
    handleUpdateApplication();
  };

  const handleDeselectPolicy = (policy: string) => {
    setEditExpanded('');
    const newPolicies = applicationTypes.includes(policy)
      ? applicationTypes.filter((p: string) => p !== policy)
      : [...applicationTypes, policy];

    setApplicationTypes(newPolicies);
  };

  const handleSelectPolicy = (policy: string) => {
    setEditExpanded('');
    setApplicationTypes([...applicationTypes, policy]);
  };

  const handlePolicyChange = (policy: string) => {
    if (applicationTypes.includes(policy)) {
      if (applicationTypes.length === 1) {
        setShowErrorMsg(policy);
        setShouldShowErrorMsg(true);
        setTimeout(() => setShowErrorMsg(''), 5000);
        setTimeout(() => setShouldShowErrorMsg(false), 6000);
      } else {
        handleDeselectPolicy(policy);
      }
    } else {
      handleSelectPolicy(policy);
    }
  };

  const incomeLimit = quotes?.[0]?.businessIncomeLimit;

  const updateHandlers = {
    updateGlLimit,
    updateBppDeductible,
    updatePlLimit,
    updatePlDeductible,
    updateBppLimit,
    updateBuildingLimit,
    updateRetroactiveDate,
  };

  const summaryValues = {
    bppLimit,
    buildingLimit,
    bppDeductible,
    glLimit,
    propertyDamageLiabilityDeductible,
    plLimit,
    plDeductible,
    bppLimitError,
    buildingLimitError,
    incomeLimit,
    deductibleOptions: applicationOptions?.bppDeductible?.allowedValues,
    windHailDeductible: feWindHailDeductibleFromApi
      ? formatWindHailDeductible(apiWindHailDeductible)
      : '5% subject to $2,500 min',
    windHailDescription:
      feWindHailDeductibleFromApi && apiWindHailDeductible
        ? windHailDeductibleDescription(apiWindHailDeductible.min)
        : undefined,
    businessIncomeDescription: feWindHailDeductibleFromApi ? toolTipContent.BUSINESS_INCOME_LIMIT : undefined,
    coastalMessages: feWindHailDeductibleFromApi
      ? {
          deductible: applicationOptions?.bppDeductible?.hasCoastalRestriction
            ? COASTAL_MESSAGES.DEDUCTIBLE
            : undefined,
          windHail: applicationOptions?.windHailDeductible?.hasCoastalRestriction
            ? COASTAL_MESSAGES.DEDUCTIBLE
            : undefined,
          // NOTE a business income limit only comes back under coastal rules, so its presence is the condition we may want to move this to options api in the future
          businessIncome: COASTAL_MESSAGES.LIMIT,
        }
      : undefined,
  };

  const SUMMARIES = summariesAttributesFactory(
    updateHandlers,
    summaryValues,
    occurrenceLimitOptions,
    isLoadingPLOptions
  );

  const getBackgroundColor = (policyType: string) => {
    if (policyType === 'GL') {
      return { borderColor: '#40FFB1', backgroundColor: '#CFFFEB' };
    } else if (policyType === 'BOP') {
      return { borderColor: '#FFDB43', backgroundColor: '#FFF4C6' };
    } else return;
  };

  const hasYearsOfPriorActsError = reasonabilityErrors.some(
    (error) => error.includes('Years of prior acts cannot exceed 6 years.') && isRetroActiveDateChecked
  );

  return (
    <>
      <PolicySectionHeader>Your Policies</PolicySectionHeader>
      {cachedSelectedPolicies.map((policyType: string, index: number) => {
        const showPolicySummary = editExpanded === policyType || (hasYearsOfPriorActsError && policyType === 'PL');
        const showHr =
          !applicationTypes.includes(policyType) || editExpanded === policyType || hasYearsOfPriorActsError;
        return (
          <FlexDiv key={policyType}>
            <SectionCont data-cy={`${policyType}-summary-card`} isSelected={applicationTypes.includes(policyType)}>
              <SectionHeaderCont>
                <Checkbox
                  onChange={() => handlePolicyChange(policyType)}
                  selected={applicationTypes.includes(policyType)}
                  ariaLabel={`${applicationTypes.includes(policyType) ? 'deselect' : 'select'} ${
                    POLICY_NAMES[policyType]
                  }`}
                />
                {shouldShowErrorMsg ? (
                  <ErrorPopUp showErrorMsg={showErrorMsg === policyType}>
                    <ExclamationMark src={coterie_bang} alt="exclamation mark" />
                    <span>You must have at least one policy activated.</span>
                  </ErrorPopUp>
                ) : null}
                <HeaderSubCont>
                  <PolicyIcon icon={policyType} onClick={() => togglePolicyEdit(policyType)} />
                </HeaderSubCont>
                <PolicyTitle>{POLICY_NAMES[policyType]}</PolicyTitle>
              </SectionHeaderCont>
              <div>
                <PolicySubtitle>{policyInfo[policyType].subtitle}</PolicySubtitle>
                <PolicyPrice>${quoteAmountByType[policyType]?.monthly}/mo</PolicyPrice>
              </div>
              <StyledSmoothCollapse expanded={showPolicySummary} allowOverflowWhenOpen>
                <PolicySummaryCont>
                  {SUMMARIES[policyType]
                    .filter((type: any) => {
                      if (type.option === POLICY_OPTIONS.RETROACTIVE_DATE && !showPlv2) return false;
                      return true;
                    })
                    .map((summary: any) =>
                      summary.value != null ? (
                        <PolicySummary
                          key={summary.option}
                          policyType={summary.option}
                          policyValue={summary.value}
                          inputValue={summary.inputValue}
                          selectedPolicies={applicationTypes}
                          expanded={expanded}
                          hasYearsOfPriorActsError={hasYearsOfPriorActsError}
                          setExpanded={setExpanded}
                          updateValue={summary.updateHandler}
                          inputType={summary.inputType}
                          isDisabled={isLoading}
                          isReadOnly={summary.isReadOnly}
                          errorMessage={summary.hasError}
                          isFlorida={hasWindHailDeductible}
                          options={summary.options}
                          isLoadingOptions={summary.isLoadingOptions}
                          description={summary.description}
                          coastalMessage={summary.coastalMessage}
                        />
                      ) : null
                    )}
                  {renderExclusions(policyType) && (
                    <PolicyItem
                      propertyName="Which operations are excluded?"
                      expanded={expanded === 'Which operations are excluded?'}
                      setExpanded={setExpanded}
                      testId="exclusions"
                      customColors={getBackgroundColor(policyType)}
                    >
                      <>
                        <ExclusionsHeader>
                          We exclude the following operations as part of this industry:
                        </ExclusionsHeader>
                        <ExclusionsList>
                          {exclusions.map((exclusion) => (
                            <ExclusionsItem key={exclusion}>{exclusion}</ExclusionsItem>
                          ))}
                        </ExclusionsList>
                      </>
                    </PolicyItem>
                  )}
                </PolicySummaryCont>
              </StyledSmoothCollapse>
              <WhiteHr isHidden={showHr} />
              <DrawerControls
                data-cy={`${policyType}-edit-drop-down`}
                data-testid={`${policyType}-edit-drop-down`}
                disabled={hasYearsOfPriorActsError}
                onClick={() => togglePolicyEdit(policyType)}
              >
                {applicationTypes.includes(policyType)
                  ? editExpanded === policyType || hasYearsOfPriorActsError
                    ? 'Close'
                    : 'See coverages'
                  : ''}
                {applicationTypes.includes(policyType) && (
                  <BlueCaret src={caretUp} alt="" $isOpen={editExpanded === policyType || hasYearsOfPriorActsError} />
                )}
              </DrawerControls>
            </SectionCont>
          </FlexDiv>
        );
      })}
      {isBindable && <AddPolicy currentQuoteAmount={quoteAmount} />}
      <SectionDivider />
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  isLoading: selectLoadingState(state),
  businessLocation: selectFirstLocation(state),
  glLimit: selectGLLimit(state),
  bppDeductible: selectBPPDeductible(state),
  propertyDamageLiabilityDeductible: selectGLDeductible(state),
  buildingLimit: selectLocationBuildingLimit(state),
  bppLimit: selectLocationBPPLimit(state),
  plLimit: selectPlLimit(state) || null,
  plDeductible: selectPlDeductible(state) || null,
  professionalLiability: selectProfessionalLiability(state),
  grossAnnualSales: selectGrossAnnualSales(state),
  applicationTypes: selectPolicyTypes(state),
  quoteAmountByType: getQuoteAmountByType(state),
  exclusions: selectExclusions(state),
  quotes: selectQuotes(state),
  reasonabilityErrors: selectReasonabilityErrors(state),
  isRetroActiveDateChecked: selectIsRetroactiveDateChecked(state),
  isBindable: isApplicationBindable(selectApplication(state)) && getCurrentPage(state) !== PAGES.PAYMENT,
  quoteAmount: getQuoteAmount(state),
});

const mapDispatchToProps = {
  setBppDeductible,
  setGlLimit,
  updateProfessionalLiability,
  updateLocation,
  setApplicationTypes,
  handleUpdateApplication,
};

const FlexDiv = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const PolicySubtitle = styled.p`
  font-family: ${(props) => props.theme.font.typeface.secondary};
`;

const BlueCaret = styled.img<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
`;

const PolicySectionHeader = styled(SectionHeader)`
  margin-bottom: 16px;
`;

export default connect(mapStateToProps, mapDispatchToProps)(PolicySummaryCards);
